import { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { LogsView } from "./components/LogsView";
import { useAuditLogs, useExternalApiLogs } from "./queries";
import {
  parseLogId,
  parseLogTab,
  toExternalLogCriteria,
} from "./schema";
import type {
  ExternalLogCriteria,
  ExternalLogFilterValues,
  LogTab,
} from "./schema";

function queryState(query: { isPending: boolean; isError: boolean }) {
  return query.isPending ? "loading" as const : query.isError ? "error" as const : "ready" as const;
}

function filtersFrom(params: URLSearchParams): ExternalLogCriteria {
  const rawLimit = params.get("limit") ?? "100";
  const limit = /^\d+$/.test(rawLimit) && Number(rawLimit) >= 1 && Number(rawLimit) <= 500
    ? Number(rawLimit)
    : 100;
  return {
    providerCode: (params.get("providerCode") ?? "").trim().toUpperCase(),
    requestId: (params.get("requestId") ?? "").trim(),
    businessRefType: (params.get("businessRefType") ?? "").trim().toUpperCase(),
    businessRefId: (params.get("businessRefId") ?? "").trim(),
    limit,
  };
}

function paramsFor(tab: LogTab, criteria: ExternalLogCriteria, logId?: number) {
  const params = new URLSearchParams();
  if (tab === "external") {
    params.set("tab", "external");
    if (criteria.providerCode) params.set("providerCode", criteria.providerCode);
    if (criteria.requestId) params.set("requestId", criteria.requestId);
    if (criteria.businessRefType) params.set("businessRefType", criteria.businessRefType);
    if (criteria.businessRefId) params.set("businessRefId", criteria.businessRefId);
    if (criteria.limit !== 100) params.set("limit", String(criteria.limit));
  }
  if (logId) params.set("logId", String(logId));
  return params;
}

export function LogsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.toString();
  const tab = parseLogTab(searchParams.get("tab"));
  const criteria = useMemo(() => filtersFrom(new URLSearchParams(search)), [search]);
  const logId = parseLogId(searchParams.get("logId"));
  const audit = useAuditLogs(tab === "audit");
  const external = useExternalApiLogs(criteria, tab === "external");
  const selectedAudit = tab === "audit" ? audit.data?.find((row) => row.id === logId) : undefined;
  const selectedExternal = tab === "external" ? external.data?.find((row) => row.id === logId) : undefined;

  useEffect(() => {
    const canonical = paramsFor(tab, criteria, logId);
    if (canonical.toString() !== search) {
      setSearchParams(canonical, { replace: true });
    }
  }, [criteria, logId, search, setSearchParams, tab]);

  const applyExternalFilters = (values: ExternalLogFilterValues) => {
    setSearchParams(paramsFor("external", toExternalLogCriteria(values)));
  };

  return <LogsView
    tab={tab}
    externalCriteria={criteria}
    audits={audit.data}
    auditState={tab === "audit" ? queryState(audit) : "idle"}
    externalLogs={external.data}
    externalState={tab === "external" ? queryState(external) : "idle"}
    selectedAudit={selectedAudit}
    selectedExternal={selectedExternal}
    previewId={logId}
    previewHref={(id) => `${location.pathname}?${paramsFor(tab, criteria, id).toString()}`}
    onTabChange={(next) => setSearchParams(paramsFor(next, criteria))}
    onExternalFiltersChange={applyExternalFilters}
    onSelect={(id) => navigate(`${location.pathname}?${paramsFor(tab, criteria, id)}`, { state: { logPreviewOriginKey: location.key } })}
    onClosePreview={() => {
      const originKey = (location.state as { logPreviewOriginKey?: string } | null)?.logPreviewOriginKey;
      if (originKey) navigate(-1);
      else setSearchParams(paramsFor(tab, criteria), { replace: true });
    }}
    onRetryAudit={() => void audit.refetch()}
    onRetryExternal={() => void external.refetch()}
  />;
}
